import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import type {
  MonitorCheckDetail,
  MonitorCheckRow,
  MonitorIncidentRow,
  MonitorSummary,
  RunIncidentView,
} from "@shared/types/monitor";
import { useTenantInstance } from "@/contexts/TenantInstanceContext";
import { apiRequest } from "@/lib/api";
import { listQueryOptions, useIsOnline } from "@/lib/listQuery";

/**
 * skyMonitor's reads (spec sections 9d and 11): the summary, the checks, one check's history
 * and the incidents. Every key carries the instance id, so one instance's
 * checks never show under another's header. skyMonitor evaluates every few minutes, so a
 * revisit reads again after a minute.
 */

export const MONITOR_BASE_PATH = "/api/v2/instances/current/monitor";
export const MONITOR_SUMMARY_PATH = `${MONITOR_BASE_PATH}/summary`;
export const MONITOR_CHECKS_PATH = `${MONITOR_BASE_PATH}/checks`;
export const MONITOR_INCIDENTS_PATH = `${MONITOR_BASE_PATH}/incidents`;

const STALE_MS = 60_000;

/**
 * #2089: while Data health (or the Overview's Data health card) is open in a visible tab, its reads
 * come again this often, so a run that finishes while it is open reaches "Last load", "last
 * reported" and the counts without a reload. skyMonitor writes them when a check falls due: its
 * tick is every minute (`MONITOR_DEFAULT_TICK_MS`) and the freshness check runs every 5 minutes
 * (`monitor/checks/registry.ts`), so after a finish the page follows within that cadence plus one
 * tick plus one poll (about 6 minutes 45 seconds at worst). A run's terminal frame would come
 * before the monitor writes and read the old values, so the page polls rather than refresh on the
 * frame. Never in a hidden tab, and every read stands down while offline.
 */
export const MONITOR_POLL_MS = 45_000;

/** The poll of a list read: off while offline, where every tick would fail (#1398). */
function useListPoll(): { refetchInterval: number | false; refetchIntervalInBackground: false } {
  const online = useIsOnline();
  return { refetchInterval: online ? MONITOR_POLL_MS : false, refetchIntervalInBackground: false };
}

export function monitorCheckPath(id: string): string {
  return `${MONITOR_CHECKS_PATH}/${encodeURIComponent(id)}`;
}

/** Whether this deployment has skyMonitor on (`GET /me` `features.monitor`). */
export function useMonitorEnabled(): boolean {
  const { me } = useTenantInstance();
  return me?.features?.monitor === true;
}

function useInstanceId(): string | null {
  return useTenantInstance().selectedInstance?.id ?? null;
}

export function useMonitorSummary(enabled: boolean): UseQueryResult<MonitorSummary> {
  const instanceId = useInstanceId();
  const poll = useListPoll();
  return useQuery<MonitorSummary>({
    queryKey: [MONITOR_SUMMARY_PATH, instanceId],
    enabled: enabled && instanceId !== null,
    queryFn: ({ signal }) => apiRequest<MonitorSummary>(MONITOR_SUMMARY_PATH, { signal }),
    staleTime: STALE_MS,
    ...poll,
  });
}

export function useMonitorChecks(enabled: boolean): UseQueryResult<MonitorCheckRow[]> {
  const instanceId = useInstanceId();
  const poll = useListPoll();
  return useQuery<MonitorCheckRow[]>({
    ...listQueryOptions,
    ...poll,
    queryKey: [MONITOR_CHECKS_PATH, instanceId],
    enabled: enabled && instanceId !== null,
    queryFn: ({ signal }) => apiRequest<MonitorCheckRow[]>(MONITOR_CHECKS_PATH, { signal }),
    staleTime: STALE_MS,
  });
}

export function useMonitorCheck(id: string | null): UseQueryResult<MonitorCheckDetail> {
  const instanceId = useInstanceId();
  const poll = useListPoll();
  return useQuery<MonitorCheckDetail>({
    ...poll,
    queryKey: [MONITOR_CHECKS_PATH, instanceId, id],
    enabled: id !== null && instanceId !== null,
    queryFn: ({ signal }) => apiRequest<MonitorCheckDetail>(monitorCheckPath(id!), { signal }),
    staleTime: STALE_MS,
  });
}

/**
 * The open incidents, or (round 7) the ones closed in the last 30 days, which say why each closed
 * (`closeReason`).
 */
export function useMonitorIncidents(
  enabled: boolean,
  status: "open" | "closed" = "open",
): UseQueryResult<MonitorIncidentRow[]> {
  const instanceId = useInstanceId();
  const path =
    status === "closed" ? `${MONITOR_INCIDENTS_PATH}?status=closed` : MONITOR_INCIDENTS_PATH;
  const poll = useListPoll();
  return useQuery<MonitorIncidentRow[]>({
    ...listQueryOptions,
    ...poll,
    queryKey:
      status === "closed"
        ? [MONITOR_INCIDENTS_PATH, instanceId, "closed"]
        : [MONITOR_INCIDENTS_PATH, instanceId],
    enabled: enabled && instanceId !== null,
    queryFn: ({ signal }) => apiRequest<MonitorIncidentRow[]>(path, { signal }),
    staleTime: STALE_MS,
  });
}

/** #842: the run page's read of the schedule's open failed-run incident that covers the run. */
export function runIncidentPath(runId: string): string {
  return `${MONITOR_BASE_PATH}/runs/${encodeURIComponent(runId)}/incident`;
}

export function useRunIncident(
  runId: string | null,
  enabled: boolean,
): UseQueryResult<RunIncidentView> {
  const instanceId = useInstanceId();
  return useQuery<RunIncidentView>({
    queryKey: [MONITOR_BASE_PATH, "runs", instanceId, runId, "incident"],
    enabled: enabled && runId !== null && instanceId !== null,
    queryFn: ({ signal }) => apiRequest<RunIncidentView>(runIncidentPath(runId!), { signal }),
    staleTime: STALE_MS,
    retry: false,
  });
}
